import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { isEmpty, isEqual, isNil, isNull } from 'lodash';
import { useParams } from 'react-router-dom';
import { ValuationAllocationModel } from 'api';
import { globalAction } from 'common/actions';
import { setAllocationInfo } from 'common/actions/allocations';
import { ERROR_403 } from 'common/config/api';
import { MISSING_MD_MESSAGE, MISSING_MD_TITLE } from 'common/constants/messages/validations';
import { UPDATE_VALUATION_DEFAULT_ERROR_MESSAGE } from 'common/constants/services/valuations';
import {
  VALUATIONS_SSV_NO_SECURITIES_MESSAGE,
  VALUATIONS_TITLE,
  VALUATIONS_VERSION_LABEL,
} from 'common/constants/valuations';
import { useCompanyBreadcrumb } from 'common/hooks';
import { UseCompanyBreadcrumbItem } from 'common/hooks/useCompanyBreadcrumb/types';
import { useStore } from 'common/store';
import { ExcelExportContextValues, UseStoreValues } from 'common/types/store';
import { ValuationByAllocation, ValuationsApproach } from 'common/types/valuation';
import {
  AllocationStatusBadge,
  LoadingSection,
  MessageBox,
  MissingMeasurementDateMessage,
} from 'components';
import { GridSkeleton } from 'components/Grid';
import ExcelExportContext from 'context/ExcelExportContext';
import { AllocationDialog } from 'layouts/Main/components/AllocationDialog';
import { CompaniesParams } from 'pages/Portfolio/types';
import { useRefreshHelpersWithContext } from 'pages/Valuations/components/RefreshGPCOption/hooks';
import { useCompanyMeasurementDateFilter, usePendingDecisionsByFirm } from 'services/hooks';
import { useGetPublicCompsByDate, useUpdateValuation } from 'services/hooks/valuations';
import {
  dbShortDate,
  getArrayValue,
  getNumberValue,
  getObjectValue,
  getStringValue,
  handleCatchError,
  throwError,
} from 'utilities';
import { ALLOCATION_FORBIDDEN_TAGLINE, ALLOCATION_FORBIDDEN_TITLE } from './common/constants/allocation';
import MarketDateDialog from './components/MarketDateDialog/MarketDateDialog';
import { MarketDateDialogSaveProps } from './components/MarketDateDialog/MarketDateDialog.props';
import { useAllocationVersionQueryString, useLoadValuation } from './hooks';
import {
  SaveValuationParams,
  TickersSymbolsByDate,
  UpdatePublicCompsApproachesParams,
  UseAllocationVersionQueryString,
  UseCompanyMeasurementDateFilter,
  UsePendingDecisionsByFirmHook,
} from './types';
import ValuationApproaches from './ValuationApproaches';

const ValuationsAllocation: FC = () => {
  const { firmSlugParam } = useParams<CompaniesParams>();
  const [tickersSymbolsByDate, setTickersSymbolsByDate] = useState<TickersSymbolsByDate | null>(null);
  const [storeValue, dispatch] = useStore() as unknown as UseStoreValues;
  const { companyInfo, firmList, valuationInfo } = storeValue;
  const allocationInfo = valuationInfo?.allocation;
  const { setCurrentSelectedValues } = useContext(ExcelExportContext) as unknown as ExcelExportContextValues;

  const { MDDialogue, selectedMeasurementDate } = (
    useCompanyMeasurementDateFilter as unknown as UseCompanyMeasurementDateFilter
  )();

  const {
    dialogProps,
    marketDialogProps,
    isForbidden,
    selectedVersion: selectedAllocationVersion,
  } = (useAllocationVersionQueryString as unknown as UseAllocationVersionQueryString)({
    selectedMeasurementDate,
    shouldShowMarketDateOnFilter: true,
    shouldShowVersionFilter: true,
  });

  const marketCurrency = useMemo(() => marketDialogProps.marketCurrency, [marketDialogProps]);

  // Add refs to track previous values
  const prevMarketDateRef = useRef(marketDialogProps.marketDate);
  const prevMarketCurrencyRef = useRef(marketDialogProps.marketCurrency);

  const shouldQueryAutomatically = useMemo(() => {
    const hasMarketDateChanged = marketDialogProps.marketDate !== prevMarketDateRef.current;
    const hasMarketCurrencyChanged = marketDialogProps.marketCurrency !== prevMarketCurrencyRef.current;

    // Update refs with current values
    prevMarketDateRef.current = marketDialogProps.marketDate;
    prevMarketCurrencyRef.current = marketDialogProps.marketCurrency;

    return Boolean(marketDialogProps.marketDate && (hasMarketDateChanged || hasMarketCurrencyChanged));
  }, [marketDialogProps]);

  const {
    getVersions,
    onClose: onCloseAllocationDialog,
    open: openAllocationDialog = false,
    versions,
  } = getObjectValue(dialogProps);

  const { decisions, displayDecisionAlerts, fetchPendingDecisions, getDisconnectDialog, isFetchingDecisions } = (
    usePendingDecisionsByFirm as unknown as UsePendingDecisionsByFirmHook
  )();

  const { getTickerListsByDate } = useRefreshHelpersWithContext();

  const { mutateAsync: updateValuation, isLoading: isUpdatingValuation } = useUpdateValuation();

  // Get Valuation, Financials, and Other Initial Data
  const {
    capTableVersions,
    cashTaxRate,
    companyMeasurementDate,
    compGroups,
    financialFiscalYearPeriods,
    financialPeriods,
    financialPeriodsPerMD,
    financials,
    financialStatementsList,
    financialVersions,
    getValuationInfo,
    hasMeasurementDate,
    isLoadingCapTableVersions,
    isLoadingCompanyMeasurementDate,
    isLoadingCompGroups,
    isLoadingEffectiveTaxRate,
    isLoadingFinancialFiscalYearPeriods,
    isLoadingFinancialStatementsList,
    isLoadingFinancialVersions,
    isLoadingHasMeasurementDate,
    isLoadingPrimaryCapTable,
    isLoadingValuationInfo,
    otherFinancialStatements,
    primaryCapTable,
    primaryFinancialStatement,
    setCashTaxRate,
    setValuation,
    valuation,
  } = useLoadValuation({
    currentAllocation: selectedAllocationVersion,
    currentMeasurementDate: selectedMeasurementDate,
  });

  useEffect(() => {
    setCurrentSelectedValues({
      valuation: valuation?.id,
    });
  }, [valuation, setCurrentSelectedValues]);

  // Get Public Comps by Date
  const {
    data: publicCompsByDate,
    isFetching: isFetchingPublicCompsByDate,
    isLoading: isLoadingPublicCompsByDate,
  } = useGetPublicCompsByDate({
    currency: marketCurrency,
    financialPeriods,
    shouldQueryAutomatically,
    tickersSymbolsByDate,
  });

  const companyBreadcrumb = useMemo(
    () => ({
      page: VALUATIONS_TITLE,
      item: selectedAllocationVersion
        ? {
          label: `${VALUATIONS_VERSION_LABEL} ${getNumberValue(
            selectedAllocationVersion?.version
          )} - ${getStringValue(selectedAllocationVersion?.name)}`,
          status: <AllocationStatusBadge />,
          readyForAudit: selectedAllocationVersion?.is_ready_for_audit,
        }
        : ({} as UseCompanyBreadcrumbItem),
    }),
    [selectedAllocationVersion]
  );

  // Set Company Breadcrumb
  useCompanyBreadcrumb(companyBreadcrumb);

  const activeFirm = useMemo(() => {
    if (!isEmpty(firmList) && !isEqual(firmList, ERROR_403) && firmSlugParam) {
      return getObjectValue(firmList?.find(firm => firm?.slug === firmSlugParam));
    }

    return null;
  }, [firmList, firmSlugParam]);

  const isLoading = useMemo(
    () =>
      [
        isFetchingDecisions,
        isLoadingCapTableVersions,
        isLoadingCompanyMeasurementDate,
        isLoadingCompGroups,
        isLoadingEffectiveTaxRate,
        isLoadingFinancialFiscalYearPeriods,
        isLoadingFinancialStatementsList,
        isLoadingFinancialVersions,
        isLoadingHasMeasurementDate,
        isLoadingPrimaryCapTable,
        isLoadingValuationInfo,
      ].some(Boolean),
    [
      isFetchingDecisions,
      isLoadingCapTableVersions,
      isLoadingCompanyMeasurementDate,
      isLoadingCompGroups,
      isLoadingEffectiveTaxRate,
      isLoadingFinancialFiscalYearPeriods,
      isLoadingFinancialStatementsList,
      isLoadingFinancialVersions,
      isLoadingHasMeasurementDate,
      isLoadingPrimaryCapTable,
      isLoadingValuationInfo,
    ]
  );

  const accessIsForbidden = useMemo(
    () => isForbidden || isEqual(allocationInfo, ERROR_403),
    [allocationInfo, isForbidden]
  );

  const saveValuation = useCallback(
    async (params: SaveValuationParams) => {
      const {
        cleanUnsavedChanges,
        currentValuationApproaches,
        setArePendingChanges,
        updatedValuation,
        relatedEntityValuations,
      } = params;

      const specifiedShareValuesApproaches = updatedValuation?.valuations_approaches?.filter(
        approach => approach?.valuations_approach_ssv
      );

      const someSpecifiedShareValuesWithoutSecurities = specifiedShareValuesApproaches?.some(
        approach => approach?.valuations_approach_ssv?.share_values?.length === 0
      );

      // Check if there are Specified Share Values without Securities
      if (someSpecifiedShareValuesWithoutSecurities) {
        return throwError(VALUATIONS_SSV_NO_SECURITIES_MESSAGE);
      }

      try {
        const valuationResponse = await updateValuation({ valuation: updatedValuation as ValuationAllocationModel });

        // Return early if valuationResponse is nullish (most likely due to a 401 error or network issue)
        if (!valuationResponse) {
          return handleCatchError(new Error(UPDATE_VALUATION_DEFAULT_ERROR_MESSAGE));
        }

        const { valuation: savedValuation } = getObjectValue(valuationResponse);

        await Promise.all(
          relatedEntityValuations?.map(relatedEntityValuation =>
            updateValuation({ valuation: relatedEntityValuation as ValuationAllocationModel })
          ) ?? []
        );

        cleanUnsavedChanges();
        setArePendingChanges(false);

        currentValuationApproaches.current = getArrayValue(
          savedValuation.valuations_approaches as ValuationsApproach[]
        );

        setValuation(savedValuation as ValuationByAllocation);

        return savedValuation as ValuationByAllocation;
      } catch (error) {
        return handleCatchError(error);
      }
    },
    [setValuation, updateValuation]
  );

  const updatePublicCompsForApproaches = useCallback(
    async (params: UpdatePublicCompsApproachesParams) => {
      const { gpcApproaches, currentApproaches, valuationDate } = params;

      const tickerListsByDate = getTickerListsByDate(gpcApproaches, currentApproaches, dbShortDate(valuationDate));
      setTickersSymbolsByDate(tickerListsByDate);
    },
    [getTickerListsByDate]
  );

  // Get Pending Decisions
  useEffect(() => {
    if (activeFirm?.id && !decisions && !isFetchingDecisions) {
      fetchPendingDecisions?.(activeFirm.id);
    }
  }, [activeFirm, decisions, fetchPendingDecisions, isFetchingDecisions]);

  // Refetch Valuation Info when Pending Decisions changes
  useEffect(() => {
    if (decisions && !isEmpty(decisions) && !isEmpty(selectedAllocationVersion)) {
      getValuationInfo().catch(handleCatchError);
    }
  }, [decisions, getValuationInfo, selectedAllocationVersion]);

  // Show Loading Progress
  useEffect(() => {
    dispatch?.(
      globalAction.showLoadingProgress(
        isLoading || (isLoadingPublicCompsByDate && isFetchingPublicCompsByDate) || isUpdatingValuation
      )
    );
  }, [dispatch, isFetchingPublicCompsByDate, isLoading, isLoadingPublicCompsByDate, isUpdatingValuation]);

  const onSaveMarketDateDialog = useCallback(
    (params: MarketDateDialogSaveProps) => {
      const { marketDate, marketCurrency } = params;
      marketDialogProps.setMarketDate(marketDate);
      marketDialogProps.setMarketCurrency(marketCurrency);
      setValuation(prev =>
        prev
          ? {
            ...prev,
            market_date: marketDate,
            market_currency: marketCurrency,
          }
          : prev
      );
    },
    [marketDialogProps, setValuation]
  );

  // Display Loading while checking for Measurement Date
  if (isLoadingHasMeasurementDate) {
    return <LoadingSection />;
  }

  // Loading Grid Skeleton while fetching data
  if (isLoading || isEmpty(valuation)) {
    return <GridSkeleton />;
  }

  if (!(isLoading || isLoadingHasMeasurementDate) && (!hasMeasurementDate || isNil(companyInfo?.measurement_date_id))) {
    return <MissingMeasurementDateMessage title={MISSING_MD_TITLE} tagline={MISSING_MD_MESSAGE} />;
  }

  // Check if user has access to Equity Allocation
  if (accessIsForbidden) {
    return <MessageBox fullWidth={false} tagline={ALLOCATION_FORBIDDEN_TAGLINE} title={ALLOCATION_FORBIDDEN_TITLE} />;
  }

  return (
    <>    
      {capTableVersions &&
        cashTaxRate &&
        companyMeasurementDate &&
        compGroups &&
        financialFiscalYearPeriods &&
        financialPeriods &&
        financialPeriodsPerMD &&
        financials &&
        financialVersions &&
        otherFinancialStatements &&
        primaryCapTable &&
        (primaryFinancialStatement || (isNull(primaryFinancialStatement) && isEmpty(financialStatementsList))) && // Custom FundOwnership
        selectedAllocationVersion &&
        selectedMeasurementDate &&
        valuation && (        
        <ValuationApproaches
          financialStatementsList={financialStatementsList}
          allocationVersion={selectedAllocationVersion}
          capTableVersions={capTableVersions}
          cashTaxRate={cashTaxRate}
          companyMeasurementDate={companyMeasurementDate}
          compGroups={compGroups}
          displayDecisionAlerts={displayDecisionAlerts}
          financialFiscalYearPeriods={financialFiscalYearPeriods}
          financialPeriods={financialPeriods}
          financialPeriodsPerMD={financialPeriodsPerMD}
          financials={financials}
          financialVersions={financialVersions}
          getDisconnectDialog={getDisconnectDialog}
          getValuationInfo={getValuationInfo}
          isLoadingPublicCompsByDate={isLoadingPublicCompsByDate}
          isUpdatingValuation={isUpdatingValuation}
          measurementDate={selectedMeasurementDate}
          otherFinancialStatements={otherFinancialStatements}
          primaryCapTable={primaryCapTable}
          primaryFinancialStatement={primaryFinancialStatement}
          publicCompsByDate={publicCompsByDate}
          saveValuation={saveValuation}
          setCashTaxRate={setCashTaxRate}
          setValuation={setValuation}
          updatePublicCompsForApproaches={updatePublicCompsForApproaches}
          valuation={valuation}
          getVersions={getVersions}
          marketDate={marketDialogProps.marketDate}
          marketCurrency={marketDialogProps.marketCurrency}
          numberOfVersions={getNumberValue(versions?.length)}
        />        
      )}      

      {MDDialogue && <MDDialogue />}

      {openAllocationDialog && (
        <AllocationDialog
          allocationInfo={allocationInfo}
          getVersions={getVersions}
          onClose={onCloseAllocationDialog}
          open
          setAllocationInfo={setAllocationInfo}
          versions={versions}
        />
      )}
      {marketDialogProps.isMarketDialogOpen && marketDialogProps.marketDate && (
        <MarketDateDialog
          open={marketDialogProps.isMarketDialogOpen}
          onClose={() => marketDialogProps.setIsMarketDialogOpen(false)}
          onSave={onSaveMarketDateDialog}
          marketDate={marketDialogProps.marketDate}
          marketCurrency={marketDialogProps.marketCurrency}
        />
      )}
    </>
  );
};

export default ValuationsAllocation;
